import { billingTelemetryHeaders } from '../utils/billing-telemetry-headers';
import { captureBillingException } from '../utils/report-error';
import { differenceInDays } from 'date-fns';
import * as Sentry from '@sentry/react';
import JWTokenHandler from '../utils/JWTokenHandler';
import hydrateProductOrCurrentPrice from './productHydrator';
import parentWindowDataChannel from './ParentWindowDataChannel';
import getCheckoutRoute from '../utils/get-checkout-route';
import getSubscriptionStatus from '../utils/get-subscription-status';
import getAutoPriceId from '../utils/get-auto-price-id';
import getPendingSubscription from '../utils/get-pending-subscription';
import safeJsonParse from '@tryghost/safe-json-parse';
import { envToBoolean } from '../utils/env';
import normalizeForceUpgradeTrialLimits from '../utils/force-upgrade-limits';
import { applyDevLimitScenario } from '../dev-scenarios/browser-overrides';

/**
 * @typedef {import('../types').Subscription} Subscription
 * @typedef {import('../types').SubscriptionIncluded} SubscriptionIncluded
 * @typedef {import('../types').SubscriptionResponse} SubscriptionResponse
 * @typedef {import('../types').User} User
 * @typedef {import('../types').BackgroundFunctionResponse} BackgroundFunctionResponse
 * @typedef {import('../types').BillingData} BillingData
 * @typedef {import('../../functions/utils/types').ErrorResponseBody} ErrorResponseBody
 * @typedef {import('../../functions/utils/types').ErrorResponse} ErrorResponse
 */

/**
 * Valid model identifiers for partial billing data fetching
 * @typedef {'users' | 'blogs' | 'subscription' | 'currentPrice' | 'currentSite' | 'currentSiteLimits' | 'currentProduct' | 'availableProducts' | 'invoices' | 'invoiceDetails' | 'latestInvoice'} BillingModel
 */

/**
 * Maps billing model identifiers to their corresponding BillingData properties
 * @typedef {object} BillingModelDataMap
 * @property {Pick<BillingData, 'user'>} users
 * @property {Pick<BillingData, 'blogs'>} blogs
 * @property {Pick<BillingData, 'subscription'>} subscription
 * @property {Pick<BillingData, 'currentPrice'>} currentPrice
 * @property {Pick<BillingData, 'currentSite'>} currentSite
 * @property {Pick<BillingData, 'currentSiteLimits'>} currentSiteLimits
 * @property {Pick<BillingData, 'availableProducts' | 'currentPrice'>} currentProduct
 * @property {Pick<BillingData, 'availableProducts' | 'currentPrice'>} availableProducts
 * @property {Pick<BillingData, 'invoices'>} invoices
 * @property {Pick<BillingData, 'invoiceDetails'>} invoiceDetails
 * @property {Pick<BillingData, 'latestInvoice'>} latestInvoice
 */

// Start listening to incoming messages from Ghost Admin
parentWindowDataChannel.subscribe('getSubscription', handleSubscriptionRequest);

// Initialise billing data from local storage if available
/** @type {BillingData|null} */
let _billingData = null;
/** @type {string|null} */
let _jwt = null;
/** @type {boolean} */
let _multiSiteUser = false;

/**
 * @description Creates an Error object suitable for logging to Sentry
 * @param {ErrorResponseBody|{error: ErrorResponseBody}} error
 * @param {string} fallbackMessage
 * @returns {Error}
 */
const createLoggingError = (error, fallbackMessage) => {
    const loggingError = new Error(error?.error?.message || error?.message || fallbackMessage);
    loggingError.originalError = error?.error || error;
    loggingError.status = error?.error?.status || error?.status;
    return loggingError;
};

const verbose = envToBoolean(process.env.REACT_APP_VERBOSE);
const cancelAtPeriodFlowEnabled = envToBoolean(process.env.REACT_APP_CANCEL_AT_PERIOD_END);

const jwTokenHandler = new JWTokenHandler({ verbose });

/** @type {Record<BillingModel, Function>} */
const BILLING_MODELS = {
    users: getBillingData.bind(),
    blogs: getBillingData.bind(),
    subscription: getBillingData.bind(),
    currentPrice: getBillingData.bind(),
    currentSite: getCurrentSite.bind(),
    currentSiteLimits: getCurrentSite.bind(),
    currentProduct: getCurrentAndAvailableProducts.bind(),
    availableProducts: getCurrentAndAvailableProducts.bind(),
    invoices: getInvoices.bind(),
    invoiceDetails: getInvoiceDetails.bind(),
    latestInvoice: getLatestInvoice.bind(),
};

/**
 * @description Makes a request to a Netlify function endpoint
 * @param {{uri: string, data: any, headers?: object, options?: object, jwt?: string, retryCount?: number, reportErrors?: boolean}} props
 * @returns {Promise<any>}
 */
const makeRequest = async ({
    uri,
    data,
    headers,
    options,
    jwt,
    retryCount = 0,
    reportErrors = true,
}) => {
    Sentry.addBreadcrumb({
        category: 'billing.request',
        message: uri.split('?')[0],
        data: { method: 'POST' },
    });
    Sentry.setTags({
        source: 'makeRequest',
    });

    if (!jwt) {
        const newJwt = await jwTokenHandler.init();
        if (!newJwt || newJwt?.error) {
            if (!newJwt) {
                const loggingError = new Error('No JWT received from token handler');
                if (reportErrors) captureBillingException(loggingError);
            } else {
                Sentry.withScope((scope) => {
                    scope.setTag('source', 'makeRequest');
                    scope.setContext('api_response', newJwt?.error);
                    scope.setTag('api_response_status', newJwt?.error?.status);
                    scope.setContext('full_error', newJwt?.error);
                    if (reportErrors) Sentry.captureMessage('Error receiving JWT');
                });
            }

            return;
        }
        jwt = newJwt;
    }

    const body = JSON.stringify({
        jwt,
        data,
        options,
    });

    // Set default headers
    if (!headers) {
        headers = {
            'Content-Type': 'application/json',
        };
    }

    if (!uri) {
        throw new Error('Endpoint must be provided');
    }

    const res = await window.fetch(uri, {
        method: 'POST',
        headers: { ...billingTelemetryHeaders(uri), ...headers },
        body,
    });

    if (!res) {
        const loggingError = new Error('No response received');
        if (reportErrors) captureBillingException(loggingError);
        throw loggingError;
    }

    Sentry.setContext('api_response', res);

    let response = null;
    const eventId = res.headers?.get('x-sentry-event-id');
    const sentryEventId = /^[a-f0-9]{32}$/.test(eventId || '') ? eventId : undefined;

    if (res?.status === 202 || res?.status === 204) {
        // Handle response code from background functions (202) or
        // DELETE request with no response body (204)
        return res;
    }

    if (!res.ok) {
        // gracefully catch Netlify function timeouts
        try {
            const text = await res.text();

            if (text.match(/timeout/i)) {
                const loggingError = new Error('Netlify function timeout');
                loggingError.originalError = text;
                if (reportErrors)
                    captureBillingException(loggingError, {
                        contexts: {
                            full_error: safeJsonParse(text),
                        },
                        tags: {
                            api_response_status: res?.status,
                        },
                    });

                const error = {
                    message:
                        'Your request timed out. Refresh the page and try again or contact support@ghost.org',
                    status: 408,
                    originalError: text,
                };
                return { error };
            }

            if (text.match(/token has expired/i) && retryCount < 1) {
                Sentry.addBreadcrumb({
                    category: 'makeRequest',
                    message: 'JWT expired',
                    level: 'debug',
                });
                jwt = await jwTokenHandler.init();
                // Make sure we update the globally stored jwt value
                _jwt = jwt;
                // Retry the request with the new JWT
                return makeRequest.call(this, {
                    uri,
                    data,
                    jwt,
                    headers,
                    options,
                    retryCount: retryCount + 1,
                    reportErrors,
                });
            }

            // Handle Stripe payment failure in grace period, all other errors need to be passed through and generalised
            if (text.match(/STRIPE_PAYMENT_FAILED/i) && _billingData?.isGrace) {
                const json = safeJsonParse(text);

                Sentry.withScope((scope) => {
                    scope.setTag('source', 'makeRequest');
                    scope.setContext('api_response', json || text);
                    scope.setContext('full_error', json || text);
                    if (reportErrors) Sentry.captureMessage('Stripe payment failed');
                });

                if (json && json.error) {
                    return {
                        error: {
                            message: `Error processing payment. ${json.error.message}`,
                            originalError: json.error,
                        },
                    };
                } else {
                    return {
                        error: {
                            message:
                                'Error processing payment. Try again or contact support@ghost.org',
                            originalError: text,
                        },
                    };
                }
            }

            return {
                error: {
                    message:
                        'Something went wrong. Refresh the page and try again or contact support@ghost.org',
                    status: res.status,
                    ...(sentryEventId && { sentryEventId }),
                    diagnostic: { message: `HTTP ${res.status} from ${uri.split('?')[0]}` },
                },
            };
        } catch (error) {
            if (reportErrors) captureBillingException(error);

            const err = {
                message: error?.error?.message || 'Error receiving billing data',
                status: error?.error?.status,
                originalError: error,
            };

            return { error: err };
        }
    } else {
        response = await res.json();
        if (response?.error && sentryEventId) response.error.sentryEventId = sentryEventId;

        if (response?.error && response?.error?.message === 'Token has expired' && retryCount < 1) {
            Sentry.addBreadcrumb({
                category: 'makeRequest',
                message: 'JWT expired',
                level: 'debug',
            });
            jwt = await jwTokenHandler.init();
            // Make sure we update the globally stored jwt value
            _jwt = jwt;
            // Retry the request with the new JWT
            return makeRequest.call(this, {
                uri,
                data,
                jwt,
                headers,
                options,
                retryCount: retryCount + 1,
                reportErrors,
            });
        }
    }

    if (verbose) {
        console.log(`POST ${uri} response:\n`, response);
    }

    return response;
};

function handleSubscriptionRequest(cb) {
    getPartialCachedBillingData('subscription').then((subscription) =>
        cb({ subscription: subscription }),
    );
}

/**
 * @description Sets Force upgrade role information and resets derived locked-dunning state in the billing data cache.
 * @param {import('../data/ParentWindowDataChannel').ForceUpgradeInfo} forceUpgradeInfo
 * @returns {Promise<BillingData>} billingData
 */
async function setForceUpgrade(forceUpgradeInfo) {
    // Set the force upgrade tag for Sentry globally. This will be updated
    // every time we check for force upgrade info again.
    Sentry.setTag('force_upgrade', forceUpgradeInfo?.forceUpgrade || false);

    _billingData = {
        ..._billingData,
        forceUpgradeNoOwner:
            forceUpgradeInfo.isOwner === false && forceUpgradeInfo.forceUpgrade === true,
        forceUpgradeOwner:
            forceUpgradeInfo.isOwner === true && forceUpgradeInfo.forceUpgrade === true,
        isForceUpgrade: forceUpgradeInfo.forceUpgrade === true,
        siteOwner: forceUpgradeInfo?.ownerUser,
    };

    if (verbose) {
        console.log('[API] billingData after setting forceUpgradeInfo', forceUpgradeInfo);
    }

    return _billingData;
}

/**
 * Fetches Force upgrade info from Ghost Admin and updates the billing-data cache.
 * @returns {Promise<BillingData|undefined>} Updated billing data when Ghost Admin returns Force upgrade info
 */
export async function getForceUpgrade() {
    const forceUpgradeInfo = await parentWindowDataChannel.getForceUpgradeInfo();
    if (verbose) {
        console.log('[API] forceUpgradeInfo', forceUpgradeInfo);
    }
    if (forceUpgradeInfo) {
        return await setForceUpgrade(forceUpgradeInfo);
    }
}

/**
 * Fetches partial billing data for a given model and updates the cache
 * @template {BillingModel} T
 * @param {T} model
 * @param {object} [data]
 * @returns {Promise<BillingModelDataMap[T]>}
 */
export async function getPartialBillingData(model, data) {
    if (!model) {
        throw new Error('Function must be called with a model identifier');
    }

    if (!BILLING_MODELS[model]) {
        throw new Error(`Not a valid model identifier '${model}'`);
    }

    const partialBillingData = await BILLING_MODELS[model].apply(data);

    _billingData = {
        ..._billingData,
        ...partialBillingData,
    };

    return _billingData[model];
}

/**
 * Returns cached partial billing data for a given model if available, otherwise fetches it
 * @template {BillingModel} T
 * @param {T} model
 * @returns {Promise<BillingModelDataMap[T]>}
 */
export async function getPartialCachedBillingData(model) {
    if (!model) {
        throw new Error('Function must be called with a model identifier');
    }

    if (!_billingData) {
        await getInitialBillingData();
    }

    if (!BILLING_MODELS[model]) {
        throw new Error(`Not a valid model identifier '${model}'`);
    }

    if (!_billingData[model]) {
        await getPartialBillingData(model);
    }

    return _billingData[model];
}

/**
 * @description Returns cached billing data if available, otherwise fetches initial data
 * @returns {Promise<BillingData>}
 */
export async function getCachedBillingData() {
    if (!_billingData) {
        return await getInitialBillingData();
    }

    if (verbose) {
        console.log('[API] return cached Billing data:', _billingData);
    }
    return _billingData;
}

/**
 * This should only be run when absolutely no subscription info on hand yet
 * This is the minimal required information to render the Dashboard
 * @returns {Promise<BillingData>} billingData
 */
export async function getInitialBillingData() {
    Sentry.setTag('source', 'getInitialBillingData');

    try {
        const jwt = await jwTokenHandler.init();

        if (!jwt || jwt?.error) {
            // TODO: handle unauthorized errors here as well. When user is not the owner,
            // Ghost Admin returns `null` as the token response, so we should actually be prepared
            // for this case and not log this as an error.
            if (jwt?.error?.message && jwt?.error?.message.match(/legacy/gim)) {
                if (verbose) {
                    console.log('[API] getInitialBillingData - Legacy user: ', jwt?.error?.message);
                }
                _multiSiteUser = true;
                Sentry.setTag('legacy', true);
            } else if (jwt?.error) {
                Sentry.withScope((scope) => {
                    scope.setTag('source', 'getInitialBillingData');
                    scope.setContext('full_error', jwt?.error);
                    Sentry.captureMessage('Error receiving JWT');
                });
            }

            _multiSiteUser = false;

            _billingData = _billingData || {};
            // User is not the owner and doesn't have permission to read the data.
            // Stop with any further action
            _billingData.error = {
                message: jwt?.error?.message || 'Error receiving billing data, no permission',
                status: jwt?.error?.status || null,
            };

            jwTokenHandler.clearSessionInterval();

            if (jwTokenHandler.jwt === null) {
                await getForceUpgrade();
            }

            return _billingData;
        }

        _jwt = jwt;

        const forceUpgradeInfo = await parentWindowDataChannel.getForceUpgradeInfo();

        if (forceUpgradeInfo) {
            await setForceUpgrade(forceUpgradeInfo);

            if (_billingData?.forceUpgradeNoOwner) {
                // don't even try fetching additional information for non-owner users
                return _billingData;
            }
        }

        // Step 1 returns users, blogs, and subscription data
        const billingData = await getBillingData({ initialRequest: true });

        if (billingData?.user?.email_address) {
            // Globally identify the user in Sentry
            Sentry.setUser({
                id: billingData.user.id,
                email: billingData.user.email_address,
                username: billingData.user.stripe_token,
            });
        }

        if (billingData?.error) {
            if (billingData.error?.status === 403) {
                if (verbose) {
                    console.log(
                        '[API] getInitialBillingData - Legacy user: ',
                        billingData.error?.message,
                    );
                }
                _multiSiteUser = true;

                Sentry.setTag('legacy', true);

                // Don't proceed further with requests, the customer is on a legacy plan and can't use the BMA
                jwTokenHandler.clearSessionInterval();
            }

            return billingData;
        }

        _multiSiteUser = false;

        const customerId =
            billingData?.subscription?.customer_id || billingData?.user?.stripe_token;

        // Step 2 returns additional billing information, that can be updated independently
        const [products, invoiceDetails, invoices, currentSite, discountDetails] =
            await Promise.all([
                getCurrentAndAvailableProducts({
                    stripePrice: _billingData?.subscription?.meta?.baseProduct?.stripe_price_id,
                }),
                getInvoiceDetails({ customerId }),
                getInvoices({ userId: _billingData?.user?.id }),
                getCurrentSite({ data: _billingData.blogs }),
                getDiscountDetails({ customerId }),
            ]);

        if (
            products?.error ||
            invoiceDetails?.error ||
            invoices?.error ||
            currentSite?.error ||
            discountDetails?.error
        ) {
            const loggingError = new Error('Failed to fetch additional billing information');
            loggingError.originalError =
                products?.error ||
                invoiceDetails?.error ||
                invoices?.error ||
                currentSite?.error ||
                discountDetails?.error;
            captureBillingException(loggingError, {
                contexts: {
                    full_error:
                        products?.error ||
                        invoiceDetails?.error ||
                        invoices?.error ||
                        currentSite?.error ||
                        discountDetails?.error,
                },
            });
        }

        _billingData = {
            ..._billingData,
            ...billingData,
            // Additional information that needs to be requested from Daisy
            ...products,
            ...invoices,
            ...currentSite,
            // Stripe endpoint
            ...invoiceDetails,
            ...discountDetails,
        };

        jwTokenHandler.maintainSession();

        // Check if we have a pending subscription in our local storage
        // and ensure it's set for incomplete and auto billing grace customers
        _billingData.pendingSubscription = getPendingSubscription(_billingData);
        _billingData.checkoutRoute = getCheckoutRoute(_billingData);
        _billingData.autoPriceId = getAutoPriceId({
            baseProduct: _billingData?.subscription?.meta?.baseProduct,
            availableProducts: _billingData?.availableProducts,
        });
    } catch (error) {
        captureBillingException(error);

        if (verbose) {
            console.log('[API] getInitialBillingData error', error);
        }
        _billingData = _billingData || {};

        jwTokenHandler.clearSessionInterval();

        _billingData.error = {
            message: error?.error?.message || 'Error receiving billing data',
            status: error?.error?.status,
            originalError: error,
        };
    }

    if (!_billingData.availableProducts && !_billingData?.error) {
        const loggingError = new Error('No available products found');
        captureBillingException(loggingError);

        return (_billingData.error = { message: 'Error receiving billing data' });
    }

    // Pass information to Ghost Admin
    await parentWindowDataChannel.sendSubscriptionData(_billingData);

    if (verbose) {
        console.log('[API] _billingData', _billingData);
    }

    return _billingData;
}

/**
 * @description Fetches fresh user, blog, and subscription data and replaces the derived status in the billing data cache.
 * @param {import('../../functions/get-subscription').GetSubscriptionData} data
 * @returns {Promise<BillingData>} billingData
 */
export async function getBillingData(options = {}) {
    /** @type {import('../../functions/get-subscription').GetSubscriptionResponse} */
    let billingDataResponse = {};

    Sentry.setTag('source', 'getBillingData');

    // Daisy uses this option to distinguish a site that needs subscription
    // activation, while still returning an existing subscription when present.
    if (_billingData.forceUpgradeOwner) {
        options.forceUpgrade = true;
    }

    if (!options?.initialRequest && _multiSiteUser) {
        if (verbose) {
            console.log('[API] getBillingData - not initial request: ', options);
        }
        return _billingData;
    }

    try {
        // Step 1 returns users, blogs, and subscription data
        billingDataResponse = await makeRequest({
            uri: '/api/get-subscription',
            options,
            jwt: _jwt,
        });

        if (billingDataResponse?.error) {
            if (billingDataResponse?.error?.status === 403) {
                if (verbose) {
                    console.log(
                        '[API] getBillingData - Legacy user: ',
                        billingDataResponse.error?.message,
                    );
                }

                _multiSiteUser = true;

                Sentry.setTag('legacy', true);
            }
            // Don't proceed further with requests, the customer is on a legacy plan and can't use the BMA
            return billingDataResponse;
        }

        _multiSiteUser = false;

        const customerId =
            billingDataResponse?.subscription?.customer_id ||
            billingDataResponse?.user?.stripe_token;

        // We need to check the latest invoice to see if there is any open. If there is, it means there's a pending update
        // that hasn't been finished.
        const latestInvoice = await getLatestInvoice({ customerId });

        // With the information about the latest invoice we can determine the correct subscription status
        const subscriptionStatus = getSubscriptionStatus(
            billingDataResponse?.subscription,
            latestInvoice,
            verbose,
        );

        if (
            latestInvoice?.payment_intent?.client_secret &&
            !latestInvoice?.payment_intent?.status === 'canceled'
        ) {
            billingDataResponse.subscription.meta.paymentIntentSecret =
                latestInvoice.payment_intent.client_secret;
        }

        if (
            billingDataResponse?.subscription?.status === 'incomplete' &&
            options?.sca === 'succeeded'
        ) {
            Sentry.addBreadcrumb({
                category: 'getBillingData_incomplete_sca_succeeded',
                message: 'Was incomplete subscription, SCA succeeded, manually setting to active',
                level: 'info',
            });

            // Manually overwrite the subscription status, as we might have a timing
            // issue with the webhook being sent and Daisy processing it. The SCA
            // was a success and the payment is completed, therefore the subscription
            // should manually be set to `active`, at least until the next refresh
            billingDataResponse.subscription.status = 'active';
        } else if (billingDataResponse?.isPending && options?.sca === 'succeeded') {
            Sentry.addBreadcrumb({
                category: 'getBillingData_pending_sca_succeeded',
                message: 'Was pending subscription, SCA succeeded, manually setting to false',
                level: 'info',
            });

            billingDataResponse.isPending = false;
        }

        // If we don't have a coupon on the user, ensure we don't provide
        // coupon details in the billing data
        if (!billingDataResponse?.user?.coupon) {
            _billingData.discount = {};
            // When we have a coupon saved on the user, but it doesn't match the one
            // we already fetched the details for, update the details for the updated
            // coupon and merge it into the billingDataResponse
        } else if (billingDataResponse?.user?.coupon !== _billingData?.discount?.coupon?.id) {
            const discountDetails = await getDiscountDetails({
                customerId: billingDataResponse?.user?.stripe_token_sg,
            });
            billingDataResponse = {
                ...billingDataResponse,
                ...discountDetails,
            };
        }

        _billingData = {
            ..._billingData,
            ...billingDataResponse,
            ...subscriptionStatus,
        };

        if (_billingData?.subscription) {
            /**
             * This is needed for Ghost Admin to render the Upgrade CTA
             * @todo: rename the property from `isActiveTrial` to something more descriptive, as it also includes
             * incomplete subscriptions and subscriptions in auto-billing grace period
             */
            _billingData.subscription.isActiveTrial =
                _billingData?.isTrial ||
                _billingData?.isIncomplete ||
                _billingData?.isAutoBillingGrace;

            // TODO: remove flag, once fully in production
            // Determine if the subscription will be cancelled at the end of the current billing period
            if (
                cancelAtPeriodFlowEnabled &&
                _billingData?.subscription?.cancelled_at &&
                !_billingData?.isTrial
            ) {
                const cancelledAt = new Date(_billingData?.subscription?.cancelled_at);
                const daysUntilCancellation = differenceInDays(cancelledAt, new Date());

                _billingData.willCancelAtPeriodEnd = daysUntilCancellation >= 1;
            }
        }

        _billingData.autoPriceId = getAutoPriceId({
            baseProduct: _billingData?.subscription?.meta?.baseProduct,
            availableProducts: _billingData?.availableProducts,
        });

        if (
            _billingData?.availableProducts &&
            _billingData?.latestInvoice &&
            _billingData?.currentSiteLimits
        ) {
            _billingData.pendingSubscription = getPendingSubscription(_billingData);
            _billingData.checkoutRoute = getCheckoutRoute(_billingData);
        }

        if (verbose) {
            console.log('[API] updated _billingData in getBillingData: ', _billingData);
        }

        if (options.updateAdmin) {
            await parentWindowDataChannel.sendSubscriptionData(_billingData);
        }

        return _billingData;
    } catch (error) {
        captureBillingException(error);
        billingDataResponse = {
            error: {
                message: error?.error?.message || 'Error receiving subscription information',
                status: error?.error?.status,
                originalError: error,
            },
        };
    }

    _billingData = {
        ..._billingData,
        ...billingDataResponse,
    };

    if (options.updateAdmin) {
        await parentWindowDataChannel.sendSubscriptionData(_billingData);
    }

    return _billingData;
}

/**
 * @description Takes an array of blogs and returns the currently active site depending on
 * the hostname as taken from the JWT and the staff limit.
 * If the current site id is already available, we use that instead and fetch
 * the data fresh from the backend
 * @param {import('../../functions/get-current-site').GetSiteUsageData} data
 * @returns {Promise<BillingData|import('../../functions/get-current-site').GetCurrentSiteResponse>}
 */
export async function getCurrentSite(data) {
    Sentry.setTag('source', 'getCurrentSite');
    Sentry.setContext('request_data', data);

    const options = {};
    /** @type {import('../../functions/get-current-site').GetCurrentSiteResponse} */
    let currentSite = {};
    let blogs = data?.blogs || _billingData?.blogs;
    let siteId = _billingData?.currentSite?.id || null;

    if (_multiSiteUser) {
        Sentry.setTag('legacy', true);
        return _billingData;
    }

    if (!blogs) {
        const billingDataResponse = await getBillingData({ initialRequest: true });
        blogs = billingDataResponse?.blogs;
        Sentry.setExtra('siteId', billingDataResponse?.blogs?.[0]?.id);
    }

    if (_billingData?.forceUpgradeOwner) {
        options.forceUpgrade = true;
    }

    try {
        currentSite = await makeRequest({
            uri: '/api/get-current-site',
            data: {
                blogs,
                siteId,
            },
            options,
            jwt: _jwt,
        });

        if (currentSite?.error?.message?.match(/special legacy plan/)) {
            jwTokenHandler.clearSessionInterval();
            // Don't proceed further with requests, the customer is on a legacy plan and can't use the BMA
            return currentSite.error;
        }

        // No-op outside the local dev server (NODE_ENV=development): replaces
        // site usage with the selected limit-scenario(s) so blocked-plan states are viewable
        currentSite = applyDevLimitScenario(currentSite);

        if (currentSite?.currentSiteLimits) {
            currentSite.currentSiteLimits = normalizeForceUpgradeTrialLimits(
                currentSite.currentSiteLimits,
                _billingData,
            );
        }

        // Check if the customer has exceeded any limits
        if (currentSite?.currentSiteLimits) {
            const limitNames = Object.keys(currentSite.currentSiteLimits);

            const exceededLimits = limitNames.filter((limit) => {
                // Case: we have a members_override set, so we need to compare the total
                // against this instead of the value of the price. This is how the limit
                // service in Ghost decides to block post publishing and we should follow
                // the same logic here.
                // If we pass objects in the `exceeded` array to Ghost Admin, Ghost will
                // render the upgrade banner, which we want to avoid in this case.
                if (limit === 'members' && currentSite.currentSiteLimits[limit]?.members_override) {
                    return (
                        currentSite.currentSiteLimits[limit]?.total >
                        currentSite.currentSiteLimits[limit]?.members_override
                    );
                } else {
                    return currentSite.currentSiteLimits[limit].exceeded;
                }
            });

            // Set exceeded limits to Sentry context
            Sentry.setContext(
                'exceeded_limits',
                exceededLimits?.reduce((acc, limit) => {
                    acc[limit] = true;
                    return acc;
                }, {}),
            );

            _billingData = {
                ..._billingData,
                ...currentSite,
                exceededLimits,
            };

            const subscriptionStatus = getSubscriptionStatus(
                _billingData.subscription,
                _billingData.latestInvoice,
            );

            const pendingSubscription = getPendingSubscription(_billingData);
            const checkoutRoute = getCheckoutRoute(_billingData);

            if (verbose) {
                console.log('[API] checkoutRoute', checkoutRoute);
            }

            _billingData = {
                ..._billingData,
                ...subscriptionStatus,
                pendingSubscription,
                checkoutRoute,
            };
        }
    } catch (error) {
        captureBillingException(error);

        /** @type {ErrorResponseBody} */
        currentSite.error = {
            message: error?.error?.message || 'Error receiving current site information',
            status: error?.error?.status,
            originalError: error,
        };
    }

    return currentSite;
}

/**
 * @description Fetches available and current products for the given stripe price ID
 * @param {import('../../functions/get-current-plan').GetCurrentPlanData} data
 * @returns {Promise<BillingData|import('../../functions/get-current-plan').GetCurrentPlanResponse>}
 */
export async function getCurrentAndAvailableProducts(data) {
    Sentry.setTag('source', 'getCurrentAndAvailableProducts');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/get-current-plan').GetCurrentPlanResponse} */
    let res = {};
    let stripePrice =
        data?.stripePrice || _billingData?.subscription?.meta?.baseProduct?.stripe_price_id;
    let availableProducts = data?.availableProducts || _billingData?.availableProducts || null;
    /** @type {string|null} */
    const productGroup = _billingData?.user?.show_product_group || null;
    let currentPrice = null;

    if (_multiSiteUser) {
        Sentry.setTag('legacy', true);
        return _billingData;
    }

    if (!stripePrice) {
        const billingData = await getBillingData();
        stripePrice = billingData?.subscription?.meta?.baseProduct?.stripe_price_id;
    }

    try {
        res = await makeRequest({
            uri: '/api/get-current-plan',
            data: {
                stripePrice,
                availableProducts,
                productGroup,
            },
            jwt: _jwt,
        });

        if (res?.availableProducts && res.availableProducts.length) {
            if (verbose) {
                console.log('[API] Hydrating available products');
            }
            availableProducts = res?.availableProducts.map(hydrateProductOrCurrentPrice);
        } else {
            const loggingError = new Error('No available products found');
            captureBillingException(loggingError);
        }

        if (res?.currentPrice && !res.currentPrice.legacy) {
            currentPrice = res.currentPrice ? hydrateProductOrCurrentPrice(res.currentPrice) : null;

            if (verbose) {
                console.log('[API] Current Price: ', currentPrice);
            }
        } else if (res?.currentPrice && res?.currentPrice?.legacy) {
            currentPrice = res.currentPrice ? hydrateProductOrCurrentPrice(res.currentPrice) : null;

            if (verbose) {
                console.log('[API] Current Legacy Price: ', currentPrice);
            }
        } else if (!res.currentPrice && !_billingData?.isForceUpgrade) {
            const loggingError = new Error('No current price found');
            captureBillingException(loggingError);
        }

        /** @type {import('../../functions/get-current-plan').GetCurrentPlanSuccess} */
        const products = {
            availableProducts,
            currentPrice,
        };

        _billingData = {
            ..._billingData,
            ...products,
        };

        return products;
    } catch (error) {
        captureBillingException(error);

        /** @type {ErrorResponseBody} */
        res.error = {
            message: error?.error?.message || 'Error receiving products and prices',
            status: error?.error?.status,
            originalError: error,
        };
    }
}

/**
 * @description Fetches the invoice details (footer) for the given customer ID
 * @param {import('../../functions/get-invoice-details').GetInvoiceDetailsData} data
 * @returns {Promise<BillingData|import('../../functions/get-invoice-details').GetInvoiceDetailsResponse>}
 */
export async function getInvoiceDetails(data) {
    Sentry.setTag('source', 'getInvoiceDetails');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/get-invoice-details').GetInvoiceDetailsResponse} */
    let res = {};
    let customerId =
        data?.customerId ||
        _billingData?.subscription?.customer_id ||
        _billingData?.user?.stripe_token;

    if (_multiSiteUser) {
        return _billingData;
    }

    if (!customerId) {
        const billingData = await getBillingData();
        customerId = billingData?.subscription?.customer_id || billingData?.user?.stripe_token;
    }

    try {
        res = await makeRequest({
            uri: '/api/get-invoice-details',
            data: {
                customerId,
            },
            jwt: _jwt,
        });
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error getting invoice details', originalError: error };
    }

    return res;
}

/**
 * @description Fetches the invoices for the given user ID
 * @param {import('../../functions/get-invoices').GetInvoicesData} data
 * @returns {Promise<BillingData|import('../../functions/get-invoices').GetInvoicesResponse>}
 */
export async function getInvoices(data) {
    Sentry.setTag('source', 'getInvoices');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/get-invoices').GetInvoicesResponse} */
    let res = {};
    let userId = data?.userId || _billingData?.user.id;

    if (_multiSiteUser) {
        return _billingData;
    }

    if (!userId) {
        const billingData = await getBillingData();
        userId = billingData?.user?.id;
    }

    try {
        res = await makeRequest({
            uri: '/api/get-invoices',
            data: {
                userId,
            },
            jwt: _jwt,
        });
    } catch (error) {
        captureBillingException(error);
        if (verbose) {
            console.log('[API] → getInvoices → error', error);
        }
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error fetching invoices', originalError: error };
    }

    return res;
}

/**
 * @description Creates a Stripe Setup Intent to set up a new payment method for the customer
 * @param {import('../../functions/setup-card').SetupCardData} data
 * @returns {Promise<BillingData|import('../../functions/setup-card').SetupCardResponse>}
 */
export async function setupIntent(data) {
    Sentry.setTag('source', 'setupIntent');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/setup-card').SetupCardResponse} */
    let res = {};

    try {
        res = await makeRequest({
            uri: '/api/setup-card',
            data,
            jwt: _jwt,
        });
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error updating credit card', originalError: error };
    }

    return res;
}

/**
 * @description Fetches the Stripe portal URL for the given customer ID and return URL
 * @param {import('../../functions/get-stripe-portal-url').GetStripePortalUrlData} data
 * @returns {Promise<BillingData|import('../../functions/get-stripe-portal-url').GetStripePortalUrlResponse>}
 */
export async function getStripePortalUrl(data) {
    Sentry.setTag('source', 'getStripePortalUrl');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/get-stripe-portal-url').GetStripePortalUrlResponse} */
    let res = {};

    try {
        res = await makeRequest({
            uri: '/api/get-stripe-portal-url',
            data,
            jwt: _jwt,
        });

        const { url: stripePortalUrl, error } = res;

        return {
            url: stripePortalUrl,
            error,
        };
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error fetching Stripe portal URL', originalError: error };
    }

    return res;
}

/**
 * @description Fetches the payment method for the customer using the Stripe API
 * @param {import('../../functions/payment-method').FetchPaymentMethodData} data
 * @returns {Promise<BillingData|import('../../functions/payment-method').FetchPaymentMethodResponse>}
 */
export async function fetchPaymentMethod(data) {
    Sentry.setTag('source', 'fetchPaymentMethod');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/payment-method').FetchPaymentMethodResponse} */
    let res = {};

    try {
        res = await makeRequest({
            uri: '/api/payment-method',
            data,
            jwt: _jwt,
        });
    } catch (error) {
        captureBillingException(error);
        res.error = { message: 'Error fetching payment method', originalError: error };
    }

    return res;
}

/**
 * @description Updates the credit card for the customer using the Stripe API
 * @param {import('../../functions/update-card').UpdateCardData} data
 * @returns {Promise<BillingData|import('../../functions/update-card').UpdateCardResponse>}
 */
export async function updateCreditCard(data) {
    Sentry.setTag('source', 'updateCreditCard');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/update-card').UpdateCardResponse} */
    let res = {};

    try {
        res = await makeRequest({
            uri: '/api/update-card',
            data,
            jwt: _jwt,
        });

        const { user, error } = res;

        _billingData = {
            ..._billingData,
            user,
            error,
        };

        return _billingData;
    } catch (error) {
        captureBillingException(error);
        // Filter out specific Stripe error message for when the customer has insufficient funds
        // but only show it to the user when they are in the grace period
        const errorMessage =
            _billingData?.isGrace && error?.message ? error.message : 'Error updating credit card';
        /** @type {ErrorResponseBody} */
        res.error = { message: errorMessage, originalError: error };
    }

    return res;
}

/**
 * @description Adds invoice details to footer of the invoice using the Stripe API
 * @param {import('../../functions/add-invoice-details').AddInvoiceDetailsData} data
 * @returns {Promise<BillingData|import('../../functions/add-invoice-details').AddInvoiceDetailsResponse>}
 */
export async function addInvoiceDetails(data) {
    Sentry.setTag('source', 'addInvoiceDetails');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/add-invoice-details').AddInvoiceDetailsResponse} */
    let res = {};

    try {
        /** @type {import('../../functions/add-invoice-details').AddInvoiceDetailsResponse} */
        res = await makeRequest({
            uri: '/api/add-invoice-details',
            data,
            jwt: _jwt,
        });

        const { invoiceDetails, error } = res;

        _billingData = {
            ..._billingData,
            invoiceDetails,
            error,
        };

        return _billingData;
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error adding invoice details', originalError: error };
    }

    return res;
}

/**
 * @description Updates the billing contact information for the customer using the Stripe API
 * @param {import('../../functions/update-billing-contact').UpdateBillingContactData} data
 * @returns {Promise<import('../../functions/update-billing-contact').UpdateBillingContactResponse>}
 */
export async function updateBillingContact(data) {
    Sentry.setTag('source', 'updateBillingContact');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/update-billing-contact').UpdateBillingContactResponse} */
    let res = {};

    try {
        res = await makeRequest({
            uri: '/api/update-billing-contact',
            data,
            jwt: _jwt,
        });

        const { user, error } = res;

        _billingData = {
            ..._billingData,
            user,
            error,
        };

        return _billingData;
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error updating billing contact', originalError: error };
    }

    return res;
}

/**
 * @description Fetches the latest invoice for the given customer ID
 * @param {import('../../functions/get-latest-invoice').GetLatestInvoiceData} data
 * @returns {Promise<import('../../functions/get-latest-invoice').GetLatestInvoiceResponse>}
 */
export async function getLatestInvoice(data) {
    Sentry.setTag('source', 'getLatestInvoice');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/get-latest-invoice').GetLatestInvoiceResponse} */
    let res = {};

    try {
        res = await makeRequest({
            uri: '/api/get-latest-invoice',
            data,
            jwt: _jwt,
        });
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error fetching latest invoice', originalError: error };
    }

    _billingData = {
        ..._billingData,
        latestInvoice: res,
        error: res?.error,
    };

    return res;
}

/**
 * @description Fetches a simulated invoice preview to show the amount due when changing plans
 * @param {import('../../functions/get-invoice-preview').GetInvoicePreviewData} data
 * @returns {Promise<import('../../functions/get-invoice-preview').GetInvoicePreviewResponse>}
 */
export async function getInvoicePreview(data) {
    Sentry.setTag('source', 'getInvoicePreview');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/get-invoice-preview').GetInvoicePreviewResponse} */
    let res = {};

    try {
        res = await makeRequest({
            uri: '/api/get-invoice-preview',
            data,
            jwt: _jwt,
        });
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error fetching invoice preview', originalError: error };
    }

    return res;
}

/**
 * @description Fetches the discount details for the given customer ID
 * @param {import('../../functions/get-discount-details').GetCustomerDiscountData} data
 * @returns {Promise<import('../../functions/get-discount-details').GetCustomerDiscountResponse>}
 */
export async function getDiscountDetails(data) {
    Sentry.setTag('source', 'getDiscountDetails');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/get-discount-details').GetCustomerDiscountResponse} */
    let res = {};

    if (!data?.customerId) {
        return res;
    }

    try {
        res = await makeRequest({
            uri: '/api/get-discount-details',
            data,
            jwt: _jwt,
        });
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error getting coupon details', originalError: error };
    }

    return res;
}

/**
 * @description Updates the coupon on a customer in Stripe
 * @param {import('../../functions/update-discount').UpdateCustomerDiscountData} data
 * @returns {Promise<import('../../functions/update-discount').UpdateCustomerDiscountResponse>}
 */
export async function updateCustomerDiscount(data) {
    Sentry.setTag('source', 'updateCustomerDiscount');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/update-discount').UpdateCustomerDiscountResponse} */
    let res = {};

    if (!data?.customerId) {
        return res;
    }

    try {
        res = await makeRequest({
            uri: '/api/update-discount',
            data,
            jwt: _jwt,
        });
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error updating customer coupon', originalError: error };
    }

    return res;
}

/**
 * @description Fetches the coupon details for the given coupon ID
 * @param {import('../../functions/get-coupon-details').GetCouponDetailsData} data
 * @returns {Promise<import('../../functions/get-coupon-details').GetCouponDetailsResponse>}
 */
export async function fetchCouponDetails(data) {
    Sentry.setTag('source', 'fetchCouponDetails');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/get-coupon-details').GetCouponDetailsResponse} */
    let res = {};

    if (!data?.couponId) {
        return res;
    }

    try {
        res = await makeRequest({
            uri: '/api/get-coupon-details',
            data,
            jwt: _jwt,
        });
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error fetching coupon details', originalError: error };
    }

    return res;
}

/**
 * @description Fetches the subscription and optionally includes cancelled subscriptions
 * @param {import('../../functions/get-customer-balance').GetCustomerBalanceData} data
 * @returns {Promise<import('../../functions/get-customer-balance').GetCustomerBalanceResponse>}
 */
export async function getCustomerBalance(data) {
    Sentry.setTag('source', 'getCustomerBalance');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/get-customer-balance').GetCustomerBalanceResponse} */
    let res = {
        balance: 0,
    };

    try {
        res = await makeRequest({
            uri: '/api/get-customer-balance',
            data,
            jwt: _jwt,
        });
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error fetching customer balance', originalError: error };
    }

    return res;
}

/**
 * @description Fetches the subscription and optionally includes cancelled subscriptions
 * @param {import('../../functions/get-subscription').GetSubscriptionData} data
 * @returns {Promise<import('../../functions/get-subscription').GetSubscriptionResponse>}
 */
export async function getSubscription(data, telemetryHeaders = undefined) {
    Sentry.setTag('source', 'getSubscription');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/get-subscription').GetSubscriptionResponse} */
    let res = {};

    try {
        res = await makeRequest({
            uri: '/api/get-subscription',
            reportErrors: !telemetryHeaders,
            data,
            jwt: _jwt,
            headers: telemetryHeaders
                ? { 'Content-Type': 'application/json', ...telemetryHeaders }
                : undefined,
        });
    } catch (error) {
        if (!telemetryHeaders) captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error fetching subscription', originalError: error };
    }

    return res;
}

/**
 * @description Changes or activates the subscription plan and updates the billing data cache.
 *              Activation applies when the owner is in Force upgrade with no current subscription.
 * @param {import('../../functions/activate-subscription').ActivateSubscriptionData | import('../../functions/change-subscription').ChangeSubscriptionData} data
 * @returns {Promise<import('../../functions/change-subscription').ChangeSubscriptionResponse|import('../../functions/activate-subscription').ActivateSubscriptionResponse>}
 */
export async function changeOrActivateSubscriptionPlan(data) {
    Sentry.setTag('source', 'changeOrActivateSubscriptionPlan');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/change-subscription').ChangeSubscriptionResponse|import('../../functions/activate-subscription').ActivateSubscriptionResponse} */
    let res = {};
    // Force upgrade identifies the owner who can act. Activate only when there
    // is no current subscription to change.
    const method =
        _billingData?.forceUpgradeOwner && !_billingData?.subscription?.id
            ? '/api/activate-subscription'
            : '/api/change-subscription';

    Sentry.setExtra('method', method);

    try {
        res = await makeRequest({
            uri: method,
            data,
            jwt: _jwt,
        });

        // Meta can contain messages, or e. g. the `client_secret` for the PaymentIntent
        // Only activateSubscription returns a SubscriptionIncluded and the user object, changeSubscription returns a SubscriptionResponse
        /** @type {{subscription: SubscriptionIncluded|SubscriptionResponse, error: ErrorResponseBody, user: User, meta?: {messages?: string[], client_secret?: string}}} */
        let { subscription, error, user, meta } = res;

        if (error) {
            const loggingError = createLoggingError(
                error,
                'Error changing or activating subscription',
            );
            captureBillingException(loggingError);
            /** @type {ErrorResponseBody} */
            return {
                error,
            };
        }

        user = user || _billingData.user;

        if (meta) {
            subscription.meta = Object.assign({}, subscription.meta, meta);
        }

        if (_billingData.subscription.current_period_end !== subscription.current_period_end) {
            // Always keep the current period end date, so we don't have to fetch it again
            subscription.current_period_end = _billingData.subscription.current_period_end;
        }

        _billingData = {
            ..._billingData,
            subscription,
            user,
            error,
        };

        const latestInvoice = await getLatestInvoice({
            customerId: subscription?.customer_id || user?.stripe_jwt,
        });
        const subscriptionStatus = getSubscriptionStatus(
            _billingData.subscription,
            latestInvoice,
            verbose,
        );

        if (_billingData?.subscription && subscriptionStatus) {
            // This is needed for Ghost Admin to render the Upgrade CTA
            _billingData.subscription.isActiveTrial =
                subscriptionStatus?.isTrial ?? _billingData?.isIncomplete;
        }

        _billingData = {
            ..._billingData,
            ...subscriptionStatus,
        };

        // If the plan change was successful, update the current Price
        if (!_billingData?.isPending) {
            _billingData.currentPrice = updateCurrentPrice({
                stripePriceId: subscription?.meta?.baseProduct?.stripe_price_id,
                availableProducts: _billingData.availableProducts,
            });
        } else {
            _billingData.pendingSubscription = getPendingSubscription(_billingData);
            _billingData.checkoutRoute = getCheckoutRoute(_billingData);
        }
        _billingData.autoPriceId = getAutoPriceId({
            baseProduct: _billingData?.subscription?.meta?.baseProduct,
            availableProducts: _billingData?.availableProducts,
        });

        if (verbose) {
            console.log(
                '[API] updated _billingData in changeOrActivateSubscriptionPlan: ',
                _billingData,
            );
        }

        // Ensure Ghost Admin knows about the updated state
        await parentWindowDataChannel.sendSubscriptionData(_billingData);

        return _billingData;
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = {
            message: error?.message || 'Error changing your subscription',
            originalError: error,
        };
    }

    return res;
}

/**
 * @description Cancels the subscription of the passed in subscription ID
 *              This endpoint is only used for immediate cancellation of a subscription
 *              and only allowed to use for trial users and customers in dunning.
 * @param {import('../../functions/cancel-background').CancelBackgroundData} data
 * @returns {Promise<BackgroundFunctionResponse|ErrorResponse>}
 */
export async function cancelAccount(data, telemetryHeaders = undefined) {
    Sentry.setTag('source', 'cancelAccount');
    Sentry.setContext('request_data', data);

    let res = {};

    try {
        /** @type {BackgroundFunctionResponse} */
        res = await makeRequest({
            uri: '/api/cancel-background',
            data,
            jwt: _jwt,
            headers: telemetryHeaders
                ? { 'Content-Type': 'application/json', ...telemetryHeaders }
                : undefined,
        });

        return res;
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error cancelling subscription', originalError: error };
    }

    return res;
}

/**
 * @description Takes the subscription ID and sets the subscription to cancel at the end
 *              of the current billing period, or to continue a subscription that was set
 *              to cancel.
 * @param {import('../../functions/cancel').CancelAtPeriodEndData} data
 * @returns {Promise<import('../../functions/cancel').CancelAtPeriodEndResponse>}
 */
export async function cancelAtPeriodEnd(data) {
    Sentry.setTag('source', 'cancelAtPeriodEnd');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/cancel').CancelAtPeriodEndResponse} */
    let res = {};

    try {
        res = await makeRequest({
            uri: '/api/cancel',
            data,
            jwt: _jwt,
        });

        if (res?.data?.cancelled_at) {
            _billingData.subscription.cancelled_at = res.data.cancelled_at;
            _billingData.willCancelAtPeriodEnd = true;
        } else if (!res.error && !res?.data?.cancelled_at) {
            _billingData.willCancelAtPeriodEnd = false;
            _billingData.subscription.cancelled_at = null;
        }

        return res;
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = {
            message: 'Error cancelling subscription at period end',
            originalError: error,
        };
    }

    return res;
}

/**
 * @description Updates or removes a custom domain for a site. When removing a custom domain,
 *              the request is handled via the update-domain endpoint. When adding or updating
 *              a custom domain, the request is handled via the update-domain-background endpoint.
 *              A background function returns an immediate 202 response, and the actual processing of the
 *              custom domain change is done in the background.
 * @param {import('../../functions/update-domain').UpdateCustomDomainData | import('../../functions/update-domain-background').UpdateDomainBackgroundData} data
 * @returns {Promise<import('../../functions/update-domain').UpdateDomainResponse> | BackgroundFunctionResponse>}
 */
export async function updateCustomDomain(data) {
    Sentry.setTag('source', 'updateCustomDomain');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/update-domain').UpdateDomainResponse | BackgroundFunctionResponse} */
    let res = {};
    // When no external domain is passed (or null) we deal with removing the custom domain.
    // This step can be done as a normal Netlify function, because the request won't time out.
    // We can also use a blocking request when we skip the verification step in Daisy (i.e. when we use Entri)
    let uri =
        data.domain && !data.skipVerification
            ? '/api/update-domain-background'
            : '/api/update-domain';

    try {
        res = await makeRequest({
            uri,
            data,
            jwt: _jwt,
        });
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error updating custom domain', originalError: error };
    }

    return res;
}

/**
 * @description Checks the custom domain for a site. This is a polling endpoint. The actual change of the
 *              custom domain is done via the update-domain-background endpoint.
 * @param {import('../../functions/check-domain').CheckDomainData} data
 * @returns {Promise<import('../../functions/check-domain').CheckDomainResponse>}
 */
export async function checkCustomDomain(data) {
    Sentry.setTag('source', 'checkCustomDomain');
    Sentry.setContext('request_data', data);

    let res = {};

    try {
        res = await makeRequest({
            uri: '/api/check-domain',
            data,
            headers: {
                'Content-Type': 'application/json',
                'Cache-Control': 'private',
            },
            jwt: _jwt,
        });
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error checking custom domain', originalError: error };
    }

    return res;
}

/**
 * @description Checks the DNS for a domain
 * @param {import('../../functions/check-domain-dns').CheckDomainDNSData} data
 * @returns {Promise<import('../../functions/check-domain-dns').CheckDomainDNSResponse>}
 */
export async function checkDomainDns(data) {
    Sentry.setTag('source', 'checkDomainDns');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/check-domain-dns').CheckDomainDNSResponse} */
    let res = {};

    try {
        res = await makeRequest({
            uri: '/api/check-domain-dns',
            data,
            headers: {
                'Content-Type': 'application/json',
                'Cache-Control': 'private',
            },
            jwt: _jwt,
        });
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error checking domain DNS', originalError: error };
    }

    return res;
}

/**
 * @description Dry-run validation of a custom domain's DNS setup. Runs Daisy's
 *              activation checks (DNS resolution, Ghost(Pro) targeting, proxy
 *              and CAA checks) without activating the domain.
 * @param {import('../../functions/validate-domain').ValidateDomainData} data
 * @returns {Promise<import('../../functions/validate-domain').ValidateDomainClientResponse>}
 */
export async function validateDomain(data) {
    Sentry.setTag('source', 'validateDomain');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/validate-domain').ValidateDomainClientResponse} */
    let res = {};

    try {
        res = await makeRequest({
            uri: '/api/validate-domain',
            data,
            headers: {
                'Content-Type': 'application/json',
                'Cache-Control': 'private',
            },
            jwt: _jwt,
        });
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error validating domain', originalError: error };
    }

    return res;
}

/**
 * @description Sets up a custom sending domain and fetches the DNS records from Mailgun
 * @param {import('../../functions/setup-sending-domain').SetupSendingDomainData} data
 * @returns {Promise<import('../../functions/setup-sending-domain').SetupSendingDomainResponse>}
 */
export async function setupSendingDomain(data) {
    Sentry.setTag('source', 'setupSendingDomain');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/setup-sending-domain').SetupSendingDomainResponse} */
    let res = {};

    try {
        res = await makeRequest({
            uri: '/api/setup-sending-domain',
            data,
            jwt: _jwt,
        });
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error setting up custom sending domain', originalError: error };
    }

    return res;
}

/**
 * @description Removes a setup sending domain from a site
 * @param {import('../../functions/remove-sending-domain').RemoveSendingDomainData} data
 * @returns {Promise<import('../../functions/remove-sending-domain').RemoveSendingDomainResponse>}
 */
export async function removeSendingDomain(data) {
    Sentry.setTag('source', 'removeSendingDomain');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/remove-sending-domain').RemoveSendingDomainResponse} */
    let res = {};

    try {
        res = await makeRequest({
            uri: '/api/remove-sending-domain',
            data,
            jwt: _jwt,
        });
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error removing custom sending domain', originalError: error };
    }

    return res;
}

/**
 * @description Verifies the sending domain status and DNS records for a site
 * @param {import('../../functions/verify-sending-domain').VerifySendingDomainData} data
 * @returns {Promise<import('../../functions/verify-sending-domain').VerifySendingDomainResponse>}
 */
export async function verifySendingDomain(data) {
    Sentry.setTag('source', 'verifySendingDomain');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/verify-sending-domain').VerifySendingDomainResponse} */
    let res = {};

    try {
        res = await makeRequest({
            uri: '/api/verify-sending-domain',
            data,
            jwt: _jwt,
        });
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error verifying custom sending domain', originalError: error };
    }

    return res;
}

/**
 * @description Checks the sending domain status and DNS records for a site
 * @param {import('../../functions/check-sending-domain').CheckSendingDomainData} data
 * @returns {Promise<import('../../functions/check-sending-domain').CheckSendingDomainResponse>}
 */
export async function checkSendingDomain(data) {
    Sentry.setTag('source', 'checkSendingDomain');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/check-sending-domain').CheckSendingDomainResponse} */
    let res = {};

    try {
        res = await makeRequest({
            uri: '/api/check-sending-domain',
            data,
            jwt: _jwt,
        });
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error checking custom sending domain', originalError: error };
    }

    return res;
}

export async function getJWT() {
    return await parentWindowDataChannel.getToken();
}

/**
 * Enables Entri integration for the current site
 * @param {import('../../functions/enable-entri').EnableEntriData} data
 * @returns {Promise<import('../../functions/enable-entri').EnableEntriResponse>}
 */
export async function enableEntri(data) {
    Sentry.setTag('source', 'enableEntri');
    Sentry.setContext('request_data', data);

    /** @type {import('../../functions/enable-entri').EnableEntriResponse} */
    let res = {};

    try {
        res = await makeRequest({
            uri: '/api/enable-entri',
            data,
            jwt: _jwt,
        });

        return res;
    } catch (error) {
        captureBillingException(error);
        /** @type {ErrorResponseBody} */
        res.error = { message: 'Error enabling Entri integration', originalError: error };
    }

    return res;
}

/**
 * @description Updates the current price of the subscription
 * @param {object} data
 * @param {import('../types').ProductWithPricesAndFeatures[]} data.availableProducts
 * @param {string} data.stripePriceId
 * @returns {import('../types').CurrentPrice}
 */
const updateCurrentPrice = ({ availableProducts, stripePriceId }) => {
    /**
     * @param {import('../types').ProductWithPricesAndFeatures[]} allProducts
     * @param {string} stripePrice
     * @returns {import('../types').CurrentPrice[]}
     */
    const findCurrentPrice = (allProducts, stripePrice) => {
        const currentPrice = [];

        allProducts.forEach((product) => {
            if (product.prices.length) {
                product.prices.forEach((price) => {
                    if (price.stripe_price_id.toString() === stripePrice.toString()) {
                        price.base_product = product.name;
                        return currentPrice.push(price);
                    }
                });
            }
        });

        return currentPrice;
    };

    const [currentPrice] = stripePriceId ? findCurrentPrice(availableProducts, stripePriceId) : [];

    return currentPrice;
};
